import * as React from "react";
import { Link } from "react-router-dom";
import styled, { css, useTheme } from "styled-components";

import {
  BOTTOM_NAVIGATION_SPACE_KEEPER_HEIGHT,
  NAVIGATION_HEADER_SPACE_KEEPER_HEIGHT,
} from "@config/consts";

import { Button, Heading, Margins, Section } from "@ui/Atoms";
import { Caret } from "@ui/Assets/Symbolicons/Caret";
import { rem } from "@ui/helpers";

import { ScrollToTop } from "@containers/ScrollToTop";

interface OptionPageLayoutActionsButtonsProps {
  hasPendingRequest: boolean;
  onSubmit?: () => void;
  backUrl: string;
  submitText: string;
  cancelText: string;
  centeredVertically?: boolean;
}

const OptionPageLayoutActionButtons = (
  props: OptionPageLayoutActionsButtonsProps,
): React.ReactElement => (
  <ButtonsWrapper centeredVertically={props.centeredVertically}>
    <Button
      variant={"blue"}
      disabled={props.hasPendingRequest}
      onClick={props.onSubmit}
      type="submit"
    >
      {props.submitText}
    </Button>

    <StyledCancel
      variant="textBlue"
      hasPendingRequest={props.hasPendingRequest}
      onClick={e => e.preventDefault()}
    >
      <Link to={props.backUrl}>{props.cancelText}</Link>
    </StyledCancel>
  </ButtonsWrapper>
);

interface Props {
  children: React.ReactNode;
  backUrl: string;
  headingText: string;
}

const OptionPageLayout = (props: Props): React.ReactElement => {
  const theme = useTheme();

  return (
    <ScrollToTop>
      <PageContainer>
        <Link to={props.backUrl}>
          <StyledHeading type="h6">
            <Margins xs={[null, null, null, "base"]}>
              <CaretLeft
                size={theme.icons.sizes.base_x5}
                variant="line"
                color={theme.palette.grey800}
              />
            </Margins>
            {props.headingText}
          </StyledHeading>
        </Link>
        <PageContent>{props.children}</PageContent>
      </PageContainer>
    </ScrollToTop>
  );
};

export { OptionPageLayout };

const StyledHeading = styled(Heading)<{ legacy?: boolean }>`
  display: flex;
  align-items: center;
  color: ${props => props.theme.palette.grey800};
  margin-left: ${props => `-${props.theme.margins.base}`};
  margin-top: ${props => `-${props.theme.margins.base}`};

  ${props =>
    props.legacy &&
    css`
      margin: ${props => props.theme.margins.base_x3} 0;
    `}
`;

const ButtonsWrapper = styled.div<{ centeredVertically?: boolean }>`
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
  align-items: center;
  margin: ${props => props.theme.margins.base_x7} 0 0;

  ${props =>
    props.centeredVertically &&
    `
    margin: ${props.theme.margins.base_x3} 0;
  `}

  & > button {
    margin-left: ${props => props.theme.margins.base};
  }
`;

const CaretLeft = styled(Caret)`
  transform: rotate(90deg);
`;

const StyledCancel = styled(Button)<{ hasPendingRequest?: boolean }>`
  text-transform: capitalize;

  ${props =>
    props.hasPendingRequest &&
    css`
      pointer-events: none;
    `}
`;

const PageContainer = styled.div`
  background: white;
  margin: 0 ${props => `-${props.theme.margins.base_x2}`};
  padding: ${props =>
    `0 ${props.theme.margins.base_x2} ${props.theme.margins.base_x2}`};
  min-height: calc(
    100vh - ${rem(NAVIGATION_HEADER_SPACE_KEEPER_HEIGHT)} -
      ${rem(BOTTOM_NAVIGATION_SPACE_KEEPER_HEIGHT)}
  );
`;

const PageContent = styled.div`
  margin-top: ${props => `-${props.theme.margins.base}`};
`;
